// 设置管理工具
export interface Settings {
  theme: 'light' | 'dark' | 'system';
  language: 'en' | 'zh';
  notifications: boolean;
  priceAlerts: boolean;
  inviteUpdates: boolean;
}

const SETTINGS_PREFIX = 'dor-settings-';
const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  language: 'zh',
  notifications: true,
  priceAlerts: true,
  inviteUpdates: true,
};

// 获取设置键名
const getSettingsKey = (key: keyof Settings): string => {
  return `${SETTINGS_PREFIX}${key}`;
};

// 保存单个设置
export const saveSetting = <K extends keyof Settings>(key: K, value: Settings[K]): void => {
  try {
    localStorage.setItem(getSettingsKey(key), JSON.stringify(value));
  } catch (error) {
          // Failed to save setting
  }
};

// 加载单个设置
export const loadSetting = <K extends keyof Settings>(key: K): Settings[K] => {
  try {
    const stored = localStorage.getItem(getSettingsKey(key));
    if (stored !== null) {
      return JSON.parse(stored);
    }
  } catch (error) {
          // Failed to load setting
  }
  return DEFAULT_SETTINGS[key];
};

// 加载所有设置
export const loadAllSettings = (): Settings => {
  const settings: Partial<Settings> = {};
  
  (Object.keys(DEFAULT_SETTINGS) as Array<keyof Settings>).forEach(key => {
    const value = loadSetting(key);
    settings[key] = value as any;
  });
  
  return settings as Settings;
};

// 重置所有设置
export const resetAllSettings = (): void => {
  try {
    (Object.keys(DEFAULT_SETTINGS) as Array<keyof Settings>).forEach(key => {
      localStorage.removeItem(getSettingsKey(key));
    });
  } catch (error) {
          // Failed to reset settings
  }
};

// 检查localStorage是否可用
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// 获取系统主题
export const getSystemTheme = (): 'light' | 'dark' => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// 应用主题设置
export const applyTheme = (theme: Settings['theme']): 'light' | 'dark' => {
  const actualTheme = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.setAttribute('data-theme', actualTheme);
  return actualTheme;
}; 